/**
 * AccountPrivacySection Component
 * Privacy settings for account visibility using Select dropdowns
 */

import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useRefreshUserProfile } from "@/features/profile/hooks/useRefreshUserProfile"
import * as userService from "@/features/profile/services/user.service"
import { useTranslation } from "@/language"

interface PrivacySettings {
  practiceRitualsVisibilityAccess: string
  teachingRitualsVisibilityAccess: string
  attendedEventsVisibilityAccess: string
  statsVisibilityAccess: string
  coachVisibilityAccess: string
}

const defaultPrivacySettings: PrivacySettings = {
  practiceRitualsVisibilityAccess: "everyone",
  teachingRitualsVisibilityAccess: "everyone",
  attendedEventsVisibilityAccess: "everyone",
  statsVisibilityAccess: "everyone",
  coachVisibilityAccess: "everyone",
}

export function AccountPrivacySection() {
  const { t } = useTranslation("settings")
  const user = useAuthStore(state => state.user)
  const { refreshUserProfile } = useRefreshUserProfile()
  const [isSaving, setIsSaving] = useState(false)
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>(defaultPrivacySettings)

  // Load current privacy settings from user
  useEffect(() => {
    if (user) {
      setPrivacySettings({
        practiceRitualsVisibilityAccess:
          typeof user.practice_rituals_visibility_access === "string"
            ? user.practice_rituals_visibility_access
            : "everyone",
        teachingRitualsVisibilityAccess:
          typeof user.teaching_rituals_visibility_access === "string"
            ? user.teaching_rituals_visibility_access
            : "everyone",
        attendedEventsVisibilityAccess:
          typeof user.attended_events_visibility_access === "string"
            ? user.attended_events_visibility_access
            : "everyone",
        statsVisibilityAccess:
          typeof user.stats_visibility_access === "string"
            ? user.stats_visibility_access
            : "everyone",
        coachVisibilityAccess:
          typeof user.coach_visibility_access === "string"
            ? user.coach_visibility_access
            : "everyone",
      })
    }
  }, [user])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await userService.updateProfile({
        practice_rituals_visibility_access: privacySettings.practiceRitualsVisibilityAccess,
        teaching_rituals_visibility_access: privacySettings.teachingRitualsVisibilityAccess,
        attended_events_visibility_access: privacySettings.attendedEventsVisibilityAccess,
        stats_visibility_access: privacySettings.statsVisibilityAccess,
        coach_visibility_access: privacySettings.coachVisibilityAccess,
      })
      await refreshUserProfile()
      toast.success(t("accountPrivacy.saveSuccess"))
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } }
      toast.error(err.response?.data?.message || t("accountPrivacy.saveError"))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Section Header */}
      <div>
        <h1 className="text-2xl font-bold text-charcoal uppercase section-heading">
          {t("accountPrivacy.title")}
        </h1>
        <p className="text-sm text-stone mt-1">{t("accountPrivacy.description")}</p>
      </div>

      <div className="flex flex-col gap-2">
        {/* Practice Rituals Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-primary">
            {t("accountPrivacy.practiceRituals")}
          </label>
          <Select
            value={privacySettings.practiceRitualsVisibilityAccess}
            onValueChange={value =>
              setPrivacySettings(prev => ({ ...prev, practiceRitualsVisibilityAccess: value }))
            }
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("accountPrivacy.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">{t("accountPrivacy.everyone")}</SelectItem>
              <SelectItem value="friends">{t("accountPrivacy.friendsOnly")}</SelectItem>
              <SelectItem value="only_me">{t("accountPrivacy.onlyMe")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Teaching Rituals Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-primary">
            {t("accountPrivacy.teachingRituals")}
          </label>
          <Select
            value={privacySettings.teachingRitualsVisibilityAccess}
            onValueChange={value =>
              setPrivacySettings(prev => ({ ...prev, teachingRitualsVisibilityAccess: value }))
            }
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("accountPrivacy.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">{t("accountPrivacy.everyone")}</SelectItem>
              <SelectItem value="friends">{t("accountPrivacy.friendsOnly")}</SelectItem>
              <SelectItem value="only_me">{t("accountPrivacy.onlyMe")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Attended Events Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-primary">
            {t("accountPrivacy.eventsAttended")}
          </label>
          <Select
            value={privacySettings.attendedEventsVisibilityAccess}
            onValueChange={value =>
              setPrivacySettings(prev => ({ ...prev, attendedEventsVisibilityAccess: value }))
            }
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("accountPrivacy.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">{t("accountPrivacy.everyone")}</SelectItem>
              <SelectItem value="friends">{t("accountPrivacy.friendsOnly")}</SelectItem>
              <SelectItem value="only_me">{t("accountPrivacy.onlyMe")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-primary">
            {t("accountPrivacy.statsVisibility")}
          </label>
          <Select
            value={privacySettings.statsVisibilityAccess}
            onValueChange={value =>
              setPrivacySettings(prev => ({ ...prev, statsVisibilityAccess: value }))
            }
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("accountPrivacy.select")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">{t("accountPrivacy.everyone")}</SelectItem>
              <SelectItem value="friends">{t("accountPrivacy.friendsOnly")}</SelectItem>
              <SelectItem value="only_me">{t("accountPrivacy.onlyMe")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Coach Visibility */}
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-primary">
            {t("accountPrivacy.coachInfo")}
          </label>
          <Select
            value={privacySettings.coachVisibilityAccess}
            onValueChange={value =>
              setPrivacySettings(prev => ({ ...prev, coachVisibilityAccess: value }))
            }
            disabled={isSaving}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("accountPrivacy.select")} />
            </SelectTrigger>
            <SelectContent align="center">
              <SelectItem value="everyone">{t("accountPrivacy.everyone")}</SelectItem>
              <SelectItem value="friends">{t("accountPrivacy.friendsOnly")}</SelectItem>
              <SelectItem value="only_me">{t("accountPrivacy.onlyMe")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="pt-5">
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            isLoading={isSaving}
            className="px-8"
          >
            {t("accountPrivacy.saveChanges")}
          </Button>
        </div>
      </div>
    </div>
  )
}
